import {Component, EventEmitter, Input, OnInit, Output, SimpleChanges} from '@angular/core';
import {
  EventItem,
  League,
  Race,
  ScoringCategorization,
  CategorizationType,
  IndividualScoringConfiguration,
  GroupScoringConfiguration, TOAST_STATE, LeagueEvents
} from "@model";
import {BehaviorSubject} from "rxjs";
import {FormArray, FormBuilder, FormControl, FormGroup, AbstractControl, ValidationErrors, ValidatorFn} from "@angular/forms";
import {TranslocoService} from "@ngneat/transloco";
import {LeagueScoringService, ToastService} from "@services";
import {ViewportScroller} from "@angular/common";
import {ActivatedRoute} from "@angular/router";
import {NgbModal, NgbModalRef} from "@ng-bootstrap/ng-bootstrap";
import {getLanguageValue, required} from "@utility";
import {AbstractComponent, ConfirmationModalComponent, TreeSelectItem, TreeSelectModal} from "@components";

export const categorizationTypesValidator: ValidatorFn = (
    control: AbstractControl
): ValidationErrors | null => {
  const formArray = control as FormArray;

  if (!formArray || formArray.length === 0) {
    return { noTypes: true };
  }

  const usedRaceIds = new Set<number>();

  for (let i = 0; i < formArray.length; i++) {
    const typeGroup = formArray.at(i) as FormGroup;
    const assignedRaces: Race[] = typeGroup.get('assignedRaces')?.value || [];
    for (const race of assignedRaces) {
      if (usedRaceIds.has(race.id!)) {
        return { duplicateRace: true };
      }
      usedRaceIds.add(race.id!);
    }
  }

  return null;
};

@Component({
  selector: 'app-league-scoring-categorization',
  templateUrl: './league-scoring-categorization.component.html',
  styleUrl: './league-scoring-categorization.component.scss',
  standalone: false
})
export class LeagueScoringCategorizationComponent extends AbstractComponent implements OnInit {

  @Input() league: League | undefined;
  @Input() leagueEvents: LeagueEvents | undefined;
  @Input() individualScoringConfigs: IndividualScoringConfiguration[] = [];
  @Input() groupScoringConfigs: GroupScoringConfiguration[] = [];
  @Input() categorizations: ScoringCategorization[] = [];
  @Output() scoringCategorizationChange: EventEmitter<void> = new EventEmitter<void>();
  @Input() disableEditing: boolean = true;

  hideForm: boolean = true;
  _currentCategorization = new BehaviorSubject<ScoringCategorization | undefined>(undefined);
  invalidCategorizations: Set<number> = new Set<number>();


  inputForm = new FormGroup({
    name: new FormControl(),
    types: this.formBuilder.array([], { validators: [categorizationTypesValidator] }),
    groupScoringConfig: new FormControl(),
    individualScoringConfig: new FormControl(),
  });

  constructor(
      public translocoService: TranslocoService,
      public leagueScoringService: LeagueScoringService,
      private scroller: ViewportScroller,
      private route: ActivatedRoute,
      private modalService: NgbModal,
      private toastService: ToastService,
      public formBuilder: FormBuilder,
  ) {
    super();
  }

  ngOnInit() {
    this.subscriptions.push(this._currentCategorization.subscribe(value => {
      if (value) {
        this.hideForm = false;
        this.initForm(value);
        this.navigateToForm();
      }
      else {
        this.hideForm = true;
        this.initForm();
      }
    }));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['categorizations']?.currentValue !== changes['categorizations']?.previousValue) {
      this.validateCategorizations();
    }
  }


  navigateToForm() {
    this.scroller.scrollToAnchor("categoryForm");
  }

  initForm(categorization?: ScoringCategorization) {
    this.inputForm.enable();
    this.inputForm.controls.name.addValidators([required]);
    this.inputForm.controls.individualScoringConfig.addValidators([required]);
    this.inputForm.controls.groupScoringConfig.addValidators([required]);

    (this.inputForm.get('types') as FormArray).clear();

    if (categorization) {
      this.inputForm.controls.name.setValue(categorization.name);
      this.inputForm.controls.individualScoringConfig.setValue(categorization.individualScoringConfig?.id);
      this.inputForm.controls.groupScoringConfig.setValue(categorization.groupScoringConfig?.id);

      if ((categorization.types?.length ?? 0) > 0) {
        categorization.types?.forEach(t => this.addCategorizationType(t));
      } else {
        this.addCategorizationType();
      }
      if (categorization.id && this.invalidCategorizations.has(categorization.id!)){
        this.markAllAsTouched(this.inputForm);
      }

    } else {
      this.inputForm.reset();
      this.addCategorizationType();
    }

    if (this.disableEditing) {
      this.inputForm.disable();
    }
    }

  get typesArray(): FormArray {
    return this.inputForm.get('types') as FormArray;
  }

  typesFormControl(i: number): FormGroup {
    return this.typesArray.controls.at(i) as FormGroup;
  }

  addCategorizationType(categorizationType?: CategorizationType) {
    const typeGroup = this.formBuilder.group({
      name: [categorizationType?.name, required],
      weightIndividual: [categorizationType?.weightIndividual ?? 100, required],
      weightTeam: [categorizationType?.weightTeam ?? 100, required],
      assignedRaces: [categorizationType?.assignedRaces || [], required],
      assignedRacesTags: this.formBuilder.control(
          categorizationType?.assignedRaces?.length
              ? categorizationType.assignedRaces.map(race =>
                  getLanguageValue(race.name, race.nameEng, race.nameIta, race.nameSlo, race.nameSr, race.nameBs, 'hr') || ''
              )
              : [],
          required
      ) as FormControl<string[]>
    });


    this.typesArray.push(typeGroup);

    typeGroup.get('assignedRaces')?.valueChanges.subscribe((races: Race[] | null) => {
      const actualRaces = races || [];
      const tags = actualRaces.map(race =>
          getLanguageValue(
              race.name, race.nameEng, race.nameIta, race.nameSlo, race.nameSr, race.nameBs, "hr"
          ) || ""
      );
      (typeGroup.get('assignedRacesTags') as FormControl<string[]>).setValue(tags);
    });

    if (categorizationType?.assignedRaces?.length) {
      const initialTags = categorizationType.assignedRaces.map(race =>
          getLanguageValue(race.name, race.nameEng, race.nameIta, race.nameSlo, race.nameSr, race.nameBs, "hr") || ""
      );
      typeGroup.get('assignedRacesTags')?.setValue(initialTags);
    }
  }


  onEdit(categorization: ScoringCategorization) {
    this._currentCategorization.next(categorization);
  }

  onAdd() {
    const categorization: ScoringCategorization = { types: [] };
    this._currentCategorization.next(categorization);
  }

  onCancel() {
    this._currentCategorization.next(undefined);
  }

  get currentCategorization(): ScoringCategorization | undefined {
    return this._currentCategorization.value;
  }

  deleteItemType(idx: number) {
    this.typesArray.removeAt(idx);
    if (!((this.typesArray.length ?? 0) > 0)) {
      this.addCategorizationType();
    }
  }

  openRaceSelection(idx: number) {
    if (!(this.leagueEvents?.events?.length ?? 0) || this.disableEditing) return;

    const selectedRacesIds: Map<number, Set<number>> = new Map();
    const disabledRaceIds: Map<number, Set<number>> = new Map();

    this.typesArray.controls.forEach((fg, index) => {
      const assignedRaces: Race[] = fg.get('assignedRaces')?.value || [];
      assignedRaces.forEach(assignedRace => {
        const event = this.leagueEvents?.events?.find(e =>
            e.racesList?.some(r => r.id === assignedRace.id)
        );
        if (!event?.id) return;

        if (index === idx) {
          if (!selectedRacesIds.has(event.id)) selectedRacesIds.set(event.id, new Set<number>());
          selectedRacesIds.get(event.id)!.add(assignedRace.id!);
        } else {
          if (!disabledRaceIds.has(event.id)) disabledRaceIds.set(event.id, new Set<number>());
          disabledRaceIds.get(event.id)!.add(assignedRace.id!);
        }
      });
    });

    let events: EventItem[] = this.leagueEvents?.events?.map(e => {
      const disabledRaces = disabledRaceIds.get(e.id!) ?? new Set<number>();
      const races = e.racesList?.filter(r => !disabledRaces.has(r.id!)) || [];
      return { ...e, racesList: races };
    }) || [];


    const eventModalItems: TreeSelectItem[] = events.filter(e => (e.racesList?.length ?? 0) > 0)
        .map(event => this.mapEventToModalItem(event, selectedRacesIds.get(event.id!))
    );

    const modalRef: NgbModalRef = this.modalService.open(TreeSelectModal, {
      centered: true,
      size: 'lg',
      windowClass: 'tree-select-modal'
    });

    modalRef.componentInstance.items = eventModalItems;
    modalRef.componentInstance.title = "league.scoring.categorization.types.assignedRaces.modal.title";
    modalRef.componentInstance.tableColumnName = "league.scoring.categorization.types.assignedRaces.modal.tableName";

    modalRef.result.then((modalSelectedMap: Map<number, Set<number>>) => {
      const assignedRaces: Race[] = [];

      this.leagueEvents?.events?.forEach(e => {
        const selectedRaces = modalSelectedMap.get(e.id!);
        if (selectedRaces?.size) {
          assignedRaces.push(
              ...(e.racesList?.filter(r => selectedRaces.has(r.id!)) || [])
          );
        }
      });

      const typeGroup = this.typesFormControl(idx);
      typeGroup.get('assignedRaces')?.setValue(assignedRaces);
    });
  }


  private mapEventToModalItem(event: EventItem, selected?: Set<number>): TreeSelectItem {
    return {
      id: event.id!,
      name: event.name,
      selected: selected ? selected.size > 0 : false,
      visible: true,
      subItems: (event.racesList ?? []).map(r => ({
        id: r.id!,
        name: r.name,
        selected: selected?.has(r.id!) ?? false,
        visible: true,
      }))
    } as TreeSelectItem;
  }

  openDeleteCategorizationModal(categorization: ScoringCategorization) {
    if (this.disableEditing) return;
    const title = `${this.translocoService.translate("league.scoring.categorization.delete.dialog.title")}: ${categorization?.name ?? ''}`;
    const text = this.translocoService.translate("league.scoring.categorization.delete.dialog.text");


    const modalRef = this.modalService.open(ConfirmationModalComponent, { centered: true });
      modalRef.componentInstance.title = title;
      modalRef.componentInstance.text = text;

      modalRef.result.then(
          () => {
            const handleSaveSuccess = () => {
              if (this.currentCategorization?.id === categorization.id) {
                this._currentCategorization.next(undefined);
              }
              this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate('league.scoring.categorization.delete.success'));
              this.scoringCategorizationChange.emit();
            }
            const handleSaveError = (errorCode?: any) => {
              this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate('league.scoring.categorization.delete.error'));
            }
            this.leagueScoringService.deleteScoringCategorization(categorization, handleSaveSuccess, handleSaveError)
          }
          , (_) => {
            /// ignore
          });
  }

  handleDropdownClick(event: Event): void {
    event.stopPropagation();
  }

  onRemoveItem(typeIndex: number, raceIndex: number) {
    const assignedRaces: Race[] = this.typesFormControl(typeIndex).get('assignedRaces')?.value || [];
    assignedRaces.splice(raceIndex, 1);
    this.typesFormControl(typeIndex).get('assignedRaces')?.setValue(assignedRaces);
  }

  onSaveCategorization() {
    if (this.disableEditing) return;
    if (!this.inputForm.valid) {
      this.markAllAsTouched(this.inputForm);
      return;
    }

    const selectedIndividualId = this.inputForm.controls.individualScoringConfig.value;
    const selectedGroupId = this.inputForm.controls.groupScoringConfig.value;

    const individualScoringConfig = this.individualScoringConfigs.find(
        config => config.id === selectedIndividualId
    );

    const groupScoringConfig = this.groupScoringConfigs.find(
        config => config.id === selectedGroupId
    );
    const categorization: ScoringCategorization = {
      ...this.currentCategorization,
      name: this.inputForm.controls.name.value,
      individualScoringConfig: individualScoringConfig || undefined,
      groupScoringConfig: groupScoringConfig || undefined,
      types: this.typesArray.value
    };

      if (this.league?.id) {
        const handleSaveSuccess = (updated: ScoringCategorization) => {
          this.toastService.showToast(TOAST_STATE.success, this.translocoService.translate(categorization.id ? 'league.scoring.categorization.edit.success' : 'league.scoring.categorization.add.success'));
          this._currentCategorization.next(undefined);
          this.scoringCategorizationChange.emit();
          if (!categorization.id) {
            this.categorizations.push(updated);
          }
        }
        const handleSaveError = (errorCode?: any) => {
          this.toastService.showToast(TOAST_STATE.danger, this.translocoService.translate(categorization.id ? 'league.scoring.categorization.edit.error' : 'league.scoring.categorization.add.error'));
        }
        this.leagueScoringService.updateScoringCategorization(this.league!.id!, categorization,  handleSaveSuccess, handleSaveError)
      }
  }

  validateCategorizations() {
    this.invalidCategorizations.clear();
    this.categorizations.forEach(value => {
      value.types?.forEach(type => {
        if (!((type.assignedRaces?.length ?? 0) > 0) && value.id) {
          this.invalidCategorizations.add(value.id!)
        }
      })
    });
  }
}
